import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Country } from 'src/app/models/country';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import {
	EditPropertyRequestBody,
	Location,
	UserProfile,
} from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import { GeoService } from 'src/app/services/geo.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { Timezone } from 'src/app/models/timezone';
import { MarkerOnMap } from 'src/app/components/map/map.component';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { ThemePalette } from '@angular/material/core';
import { NgModel, FormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/list';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass, AsyncPipe } from '@angular/common';
import { MapComponent } from '../../../../components/map/map.component';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { HasSystemCapabilityDirective } from '../../../../directives/system-capabilities/has-system-capability.directive';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatIcon } from '@angular/material/icon';

@Component({
	selector: 'app-property-about',
	templateUrl: './property-about.component.html',
	styleUrls: ['./property-about.component.scss', '../../../common/main.scss'],
	imports: [
		MatDivider,
		MatChipSet,
		MatChip,
		NgClass,
		MapComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		MatAutocompleteTrigger,
		MatAutocomplete,
		MatHint,
		MatButton,
		HasSystemCapabilityDirective,
		AsyncPipe,
	],
})
export class PropertyAboutComponent implements OnInit {
	private readonly id: string;
	readonly activeOptions = [
		{
			value: true,
			label: $localize`:@@propertyAbout-activeOptionActive:Active`,
		},
		{
			value: false,
			label: $localize`:@@propertyAbout-activeOptionInactive:Inactive`,
		},
	];
	stateFieldOutlineColor = 'primary' as ThemePalette;
	editPropertyInfo = false;
	canEditProperties: boolean;
	timezone!: Timezone[];
	filteredCountries!: Observable<Country[]>;
	userProfile?: UserProfile;
	activeCompanyId?: string;
	loggedInCompanyIsSentriLock: boolean = false;
	loggedInCompanyIsPropertyCompany: boolean = false;

	longitudeLabel = '';
	latitudeLabel = '';
	showCountryError = false;

	statusActive = $localize`:@@propertyDetails-StatusActive:Active`;
	statusInactive = $localize`:@@propertyDetails-StatusInactive:Inactive`;
	latitudeGenFromAddress = $localize`:@@propertyDetails-LatitudeGenFromAddress:Latitude (or generate from address)`;
	localizeLatitude = $localize`:@@propertyDetails-Latitude:Latitude`;
	longitudeGenFromAddress = $localize`:@@propertyDetails-LongitudeGenFromAddress:Longitude (or generate from address)`;
	localizeLongitude = $localize`:@@propertyDetails-Longitude:Longitude`;

	successMessage = $localize`:@@propertyDetails-SuccessMessage:Successfully updated property.`;

	isUnitTesting: boolean = false; // variable for unit testing only

	constructor(
		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly bannerMessageService: BannerMessageService,
		private readonly scService: SystemCapabilityService,
		public geoService: GeoService,
		public profileService: UserProfileService
	) {
		this.id = this.route.snapshot.params.id;
		this.canEditProperties = this.scService.has(
			SystemCapabilityEnum.propertyManagement
		);

		this.longitudeLabel = this.localizeLongitude;
		this.latitudeLabel = this.localizeLatitude;
	}

	@Input() propertyId = '';
	@ViewChild('stateProvinceRegionFieldModel')
	stateProvinceRegionFieldModel!: NgModel;

	property!: Location;

	editProperty: any = {};
	pendingInstalls = 0;

	static SENTRILOCK_HQ_LAT: any = 39.363522;
	static SENTRILOCK_HQ_LNG: any = -84.366302;

	propertyMarker: MarkerOnMap[] = [{}] as MarkerOnMap[];
	mapCenter: google.maps.LatLngLiteral = {} as google.maps.LatLngLiteral;

	ngOnInit(): void {
		this.requestProperty().then(() => {});
	}

	async requestProperty(): Promise<void> {
		this.userProfile = await this.profileService.getUserProfile();
		this.activeCompanyId = this.userProfile?.activeRoleDetails?.company?.id;
		this.property = await this.apiService.getProperty(this.propertyId);
		this.pendingInstalls = this.property.pendingInstalls ?? 0;
		this.editProperty = {
			name: this.property.name,
			addressId: this.property.addressId,
			address1: this.property.address?.address1,
			address2: this.property.address?.address2,
			locality: this.property.address?.locality,
			stateProvinceRegion: this.property.address?.stateProvinceRegion,
			postalCode: this.property.address?.postalCode,
			country: this.property.address?.countryCode,
			latitude: this.property.address?.geoCoordinates?.latitude,
			longitude: this.property.address?.geoCoordinates?.longitude,
			active: this.property.active,
			pendingInstalls: this.property.pendingInstalls,
			timezone: this.property.timezone,
		};

		this.setMapCenter();
		this.setMapPin();

		const sentrilockCompanyId = await this.apiService.getSentrilockCompanyId();
		this.loggedInCompanyIsSentriLock =
			this.activeCompanyId === sentrilockCompanyId;
		this.loggedInCompanyIsPropertyCompany =
			this.property.companyId === this.activeCompanyId;
	}

	isValid(): boolean {
		const requiredAddressFields = [
			this.editProperty.address1,
			this.editProperty.locality,
			this.editProperty.stateProvinceRegion,
			this.editProperty.postalCode,
			this.editProperty.country,
		];

		const addressComplete = requiredAddressFields.reduce(
			(accumulator, value) => (value?.length ?? 0) > 0 && accumulator,
			true
		);

		const requiredCoordinatesFields = [
			this.editProperty.latitude,
			this.editProperty.longitude,
		];

		const coordinatesComplete = requiredCoordinatesFields.reduce(
			(accumulator, value) => (value?.length ?? 0) > 0 && accumulator,
			true
		);

		if (addressComplete && coordinatesComplete) {
			return this.validateGeoCoordinates(
				this.editProperty.latitude as string,
				this.editProperty.longitude as string
			);
		}

		if (addressComplete) {
			return true;
		}

		if (coordinatesComplete) {
			return this.validateGeoCoordinates(
				this.editProperty.latitude as string,
				this.editProperty.longitude as string
			);
		}

		if (this.editProperty.stateProvinceRegion.length === 0) {
			setTimeout(() => {
				this.stateProvinceRegionFieldModel.control.markAsTouched();
			});
		}

		return false;
	}

	filterCountries(value: any): void {
		this.filteredCountries = of(this._filter(value));
		this.onAddressChanged();
	}

	saveEdits(): void {
		this.dialog
			.open(PropertyDetailEditConfirmationComponent, {
				panelClass: 'pop-up-dialog',
				data: {
					editProperty: this.editProperty,
					pendingInstalls: this.pendingInstalls,
					propertyId: this.propertyId,
				},
			})
			.afterClosed()
			.subscribe(async (dialogResult: any) => {
				if (dialogResult) {
					await this.requestProperty();
					this.bannerMessageService.sendMessage(
						this.successMessage,
						undefined,
						'propertyDetails-SuccessMessage'
					);
					this.editPropertyInfo = false;
				}
			});
	}

	incrementOnClick(): void {
		if (this.pendingInstalls >= 1000) {
			this.pendingInstalls = 1000;
		} else {
			this.pendingInstalls = this.pendingInstalls + 1;
		}
	}

	decrementOnClick(): void {
		if (this.pendingInstalls <= 0) {
			this.pendingInstalls = 0;
		} else {
			this.pendingInstalls = this.pendingInstalls - 1;
		}
	}

	editButtonClicked(): void {
		this.editPropertyInfo = !this.editPropertyInfo;
	}

	editButtonCancelClicked(): void {
		this.editPropertyInfo = !this.editPropertyInfo;
		this.pendingInstalls = this.property.pendingInstalls ?? 0;
		this.editProperty = {
			name: this.property.name,
			addressId: this.property.addressId,
			address1: this.property.address?.address1,
			address2: this.property.address?.address2,
			locality: this.property.address?.locality,
			stateProvinceRegion: this.property.address?.stateProvinceRegion,
			postalCode: this.property.address?.postalCode,
			country: this.property.address?.countryCode,
			latitude: this.property.address?.geoCoordinates?.latitude,
			longitude: this.property.address?.geoCoordinates?.longitude,
			active: this.property.active,
			pendingInstalls: this.property.pendingInstalls,
			timezone: this.property.timezone,
		};

		this.setMapCenter();
		this.setMapPin();
	}

	private _filter(value: string): Country[] {
		const filterValue = value.toLowerCase();
		return this.geoService.allCountries.filter((country) =>
			country.name.toLowerCase().includes(filterValue)
		);
	}

	displayCountryName = (twoLetterCode: string): string => {
		let foundCountry = null;
		if (this.geoService?.allCountries) {
			foundCountry = this.geoService.allCountries.find(
				(country) => country.twoLetterCode === twoLetterCode
			);
		}

		return foundCountry && foundCountry.name ? foundCountry.name : '';
	};

	openAutocompleteCountries() {
		this.filteredCountries = of(this.geoService.allCountries);
	}

	countrySelected() {
		this.showCountryError = false;
	}

	countryInput() {
		this.showCountryError = true;
	}

	displayStatusName(status: boolean): string {
		if (status) {
			return this.statusActive;
		} else {
			return this.statusInactive;
		}
	}

	getChipStyling(status: boolean): string {
		if (status) {
			return 'status-chip-active';
		} else {
			return 'status-chip-inactive';
		}
	}

	setLongitudeLabel(): void {
		if (this.editProperty.longitude === '') {
			this.longitudeLabel = this.longitudeGenFromAddress;
		} else {
			this.longitudeLabel = this.localizeLongitude;
		}
	}

	setLatitudeLabel(): void {
		if (this.editProperty.latitude === '') {
			this.latitudeLabel = this.latitudeGenFromAddress;
		} else {
			this.latitudeLabel = this.localizeLatitude;
		}
	}

	onAddressChanged(): void {
		this.editProperty.latitude = '';
		this.editProperty.longitude = '';
		this.setMapCenter();
		this.setMapPin();

		this.setLatitudeLabel();
		this.setLongitudeLabel();
	}

	validateGeoCoordinates(latitude: string, longitude: string): boolean {
		// If there are no lat and long values return true
		if (latitude === '' || longitude === '') {
			return true;
		}

		// If there are lat and long values, check to see if they are in correct lat/long format
		const lat = parseInt(latitude, 10);
		const lng = parseInt(longitude, 10);

		if (isNaN(lat) || lat === undefined || isNaN(lng) || lng === undefined) {
			return false;
		}

		if (lat < -90 || lat > 90) {
			return false;
		} else if (lng < -180 || lng > 180) {
			return false;
		}
		return true;
	}

	setMapCenter() {
		if (this.editProperty.latitude && this.editProperty.longitude) {
			this.mapCenter = {
				lat: this.editProperty.latitude,
				lng: this.editProperty.longitude,
			};
		} else {
			// Set map center to Sentrilock HQ
			this.mapCenter = {
				lat: PropertyAboutComponent.SENTRILOCK_HQ_LAT,
				lng: PropertyAboutComponent.SENTRILOCK_HQ_LNG,
			};
		}
	}

	setMapPin() {
		if (this.editProperty.latitude && this.editProperty.longitude) {
			this.propertyMarker = [
				{
					position: {
						lat: this.editProperty.latitude,
						lng: this.editProperty.longitude,
					},
				},
			];
		}
	}
}

@Component({
	selector: 'app-property-detail-edit-confirmation-dialog',
	templateUrl: './property-about-dialog.component.html',
	styleUrls: ['./property-about.component.scss', '../../../common/main.scss'],
	imports: [
		MatDivider,
		CdkScrollable,
		MatDialogContent,
		MatIcon,
		MatDialogActions,
		MatButton,
	],
})
export class PropertyDetailEditConfirmationComponent {
	editProperty: any = {};
	pendingInstalls = 0;
	propertyId = '';

	showMessage = true;
	showLoading = false;
	showError = false;

	constructor(
		private dialogRef: MatDialogRef<PropertyDetailEditConfirmationComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			editProperty: any;
			pendingInstalls: number;
			propertyId: string;
		},
		private readonly apiService: ApiService
	) {
		this.editProperty = data.editProperty;
		this.pendingInstalls = data.pendingInstalls;
		this.propertyId = data.propertyId;
	}

	onCancelClicked(): void {
		this.dialogRef.close(false);
	}

	onConfirmClicked(): void {
		this.showMessage = false;
		this.showLoading = true;
		const requestBody: EditPropertyRequestBody = {
			name: this.editProperty.name,
			address: {
				id: this.editProperty.addressId,
				address1: this.editProperty.address1 ?? '',
				address2: this.editProperty.address2 ?? '',
				locality: this.editProperty.locality ?? '',
				stateProvinceRegion: this.editProperty.stateProvinceRegion ?? '',
				postalCode: this.editProperty.postalCode ?? '',
				countryCode: this.editProperty.country ?? '',
				geoCoordinates:
					!this.editProperty.latitude || !this.editProperty.longitude
						? undefined
						: {
								latitude: this.editProperty.latitude,
								longitude: this.editProperty.longitude,
							},
			},
			status: this.editProperty.active ? '1' : '0',
			pendingInstalls: this.pendingInstalls ?? 0,
			timezone: this.editProperty.timezone,
		};

		this.apiService
			.editPropertyById(this.propertyId, requestBody)
			.then((_) => {
				this.dialogRef.close(true);
			})
			.catch((err) => {
				this.showLoading = false;
				this.showError = true;
			});
	}

	getApiService() {
		return this.apiService;
	}
}
